/**
 * Communication-Specific Actions
 * Actions defined specifically for the communications entity.
 */

import type { Communications } from "@/src/gql/graphql";
import type { DetailActionStrict } from "@/lib/actions/types/detail-action";
import { ROLE_COMBINATIONS } from "@/lib/actions/types/action-properties";
import {
  universalDeleteAction,
  universalSendEmailAction,
  universalLogActivityAction,
  universalAskMeridianAction,
  universalMakeTicketAction,
  universalViewCompanyAction,
  universalCreateTaskAction,
  universalScheduleMeetingAction,
  universalViewDetailsAction,
  universalConfigureAutomatedTasksAction,
} from "@/lib/actions/definitions/universal-actions";
import { createStatusAction, createSendTextMessageAction } from "@/lib/actions/definitions/shared";
import {
  createSendToSlackAction,
  createSendToSalesChannelAction,
  createOpenSlackChannelAction,
} from "@/lib/actions/definitions/shared/slack-actions";

export const communicationDetailActions: DetailActionStrict<Communications>[] = [
  // AI ASSISTANT ACTIONS
  universalAskMeridianAction<Communications>(),

  // PRIMARY ACTIONS (Header)
  createStatusAction<Communications>(),
  // No Edit action — fields are edited inline on the detail page; the old
  // modal-based edit was an unmounted no-op (see deal-actions for the full note).
  universalSendEmailAction<Communications>(),
  universalLogActivityAction<Communications>(),

  // TABLE-ONLY ACTIONS
  universalViewDetailsAction<Communications>("viewDetails"),

  // SECONDARY ACTIONS (Menu)
  createSendTextMessageAction<Communications>(),
  universalCreateTaskAction<Communications>(),
  universalMakeTicketAction<Communications>(),
  universalScheduleMeetingAction<Communications>(),
  universalViewCompanyAction<Communications>(),
  universalConfigureAutomatedTasksAction<Communications>(),
  createSendToSlackAction<Communications>(),
  createSendToSalesChannelAction<Communications>(),
  createOpenSlackChannelAction<Communications>(),
  {
    id: "claim-communication-attribution",
    label: "Attribute to me",
    icon: "user",
    variant: "outline",
    action: "claimCommunicationAttribution",
    allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    tooltip:
      "Set yourself as credited for this communication when it synced under the wrong person.",
  },
  {
    id: "reassign-communication-sales-attribution",
    label: "Set sales attribution…",
    icon: "share",
    variant: "outline",
    action: "reassignCommunicationSalesAttribution",
    allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    tooltip:
      "Credit another teammate so reporting and pipelines reflect who actually communicated.",
  },
  {
    id: "save-meeting-to-drive",
    label: "Save summary to Drive",
    icon: "file-text",
    variant: "outline",
    action: "saveMeetingToDrive",
    allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    tooltip:
      "File this meeting's summary into the company's Joint-Meetings Google Drive folder.",
    // Meeting-type communications linked to a company only.
    visible: (item) =>
      (item.type_label ?? "").toLowerCase() === "meeting" &&
      typeof item.company_id === "string" &&
      item.company_id.length > 0,
  },
  universalDeleteAction<Communications>(),
];
